
import { useState, useEffect, useContext, createContext, useCallback, useRef } from "react";

import { useSession, useSessionUpdate } from "../context/SessionContext";
import { useNotificationUpdate } from "./NotificationContext";
import { useProfileUpdate } from "./ProfileContext";

import { fetchModelResponse, fetchAssistantResponse } from "../services/fetch";

import { QUESTIONS_PAUSED_NOTIFICATION, SERVER_ERROR_TEXT, SERVER_ORIGIN, USE_ASSISTANTS, USE_CURRENT_SLIDE_CONTEXT_ONLY, APP, ENVIRONMENT } from "../common/constants";
import { MessageType, PromptType, PromptTemplateType } from "../types/enums";


// ============================= INTERFACES =============================
interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  template: string;
  response?: string;
  category: string;
}

interface DynamicPromptData {
  slideNumber?: number;
  presentationContext?: string;
  currentSlideContext?: string;
  transcript?: string;
  currentSlideTranscript?: string;
  senderName?: string;
  studentName?: string;
  [key: string]: any; // Allow additional dynamic fields
}

interface PromptContextType {
  promptTemplates: PromptTemplate[];
  promptsFetched: boolean;
}

interface PromptUpdateContextType {
  handleQuestion: (question: string, senderName: string, selectedFile?: File) => any;
  handleGeneralPrompt: (prompt: string) => any;
  handleGeneratePrompt: (prompt: string) => any;
  handleReviseGeneratedPrompt: (prompt: string, generatedPrompt: string) => any;
  handleGenerateAndRevisePrompt: (prompt: string, revisePrompt: string) => any;
  handleExecutePrompt: (prompt: string) => any;
  handleCompileAndExecutePrompt: (promptId: string, dynamicData: DynamicPromptData, promptType?: PromptTemplateType) => any;

  getPromptCategories: (templateType?: PromptTemplateType) => string[];
  getPromptTemplate: (id: string, templateType?: PromptTemplateType) => PromptTemplate | undefined;
  getPromptTemplatesForCategory: (category: string, templateType?: PromptTemplateType) => PromptTemplate[];
  getCompiledPrompt: (id: string, data?: DynamicPromptData, templateType?: PromptTemplateType) => string;
  updatePromptTemplate: (id: string, newTemplate: string, templateType?: PromptTemplateType) => void;
  updatePromptTemplateResponse: (id: string, newResponse: string, templateType?: PromptTemplateType) => void;

  handleCompileGenerateAndRevisePrompts: (draftPromptId: string, draftDynamicData: DynamicPromptData, revisePromptId: string, reviseDynamicData: DynamicPromptData) => any;
  handleCompileGenerateAndPublishPrompts: (draftPromptId: string, draftDynamicData: DynamicPromptData, publishPromptId: string, publishDynamicData: DynamicPromptData) => any;

  getAllPrompts: (templateType?: PromptTemplateType) => PromptTemplate[];
  savePromptsToDatabase: (platform: string, environment: string) => Promise<boolean>;
}

interface ComponentProps {
  children: React.ReactNode;
}


// ============================= LOAD DEFAULT PROMPTS =============================
const defaultPrompts: PromptTemplate[] = require("../data/prompts/explicitPrompts.json");
const defaultPromptsToGeneratePrompt: PromptTemplate[] = require("../data/prompts/generatedPrompts.json");


// ============================= CONTEXT SETUP =============================
const PromptContext = createContext<PromptContextType | null>(null);
const PromptUpdateContext = createContext<PromptUpdateContextType | null>(null);


function usePrompt() {
  const context = useContext(PromptContext);
  if (!context) throw new Error("usePrompt must be used within PromptContextProvider");
  return context;
}

function usePromptUpdate() {
  const context = useContext(PromptUpdateContext);
  if (!context) throw new Error("usePromptUpdate must be used within PromptContextProvider");
  return context;
}


// ============================= PROVIDER =============================
function PromptContextProvider({ children }: ComponentProps) {

  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>(defaultPrompts);
  const [promptTemplatesGenerated, setPromptTemplatesGenerated] = useState<PromptTemplate[]>(defaultPromptsToGeneratePrompt);

  const [promptsFetched, setPromptsFetched] = useState(false);

  const promptTemplatesRef = useRef<PromptTemplate[]>(defaultPrompts);
  const promptTemplatesGeneratedRef = useRef<PromptTemplate[]>(defaultPromptsToGeneratePrompt);

  const userHasEditedRef = useRef<boolean>(false);
  const initialFetchCompleteRef = useRef<boolean>(false);
  const contextCache = useRef<Map<string, string>>(new Map());

  const { questionsAllowed } = useSession();

  const { setSlideReadyToMoveOn, getCurrentSlideNumber, getSessionTranscript, getSessionPresentationContext, getSlideContext, getDefaultAppStateHandlerConfig, setChatMessagePending } = useSessionUpdate();

  const { postNotification } = useNotificationUpdate();

  const { getHumanStudentName } = useProfileUpdate();


  // ============================= FETCH PROMPTS FROM BACKEND =============================
  // Uses new platform config API with hybrid fallback (Database -> Google Sheets -> JSON)
  useEffect(() => {
    if (initialFetchCompleteRef.current) return;

    const fetchGeneratedPrompts = async () => {
      try {
        // Use new platform config API endpoint with hybrid fallback
        const response = await fetch(`${SERVER_ORIGIN}/api/platform/config/prompts?platform=${APP}&environment=${ENVIRONMENT}&promptTemplateType=GENERATED`);
        const data = await response.json();

        if (data.prompts && Object.keys(data.prompts).length > 0) {
          console.log(`[PromptContext] Loaded generated prompts from ${data.source || 'backend'}`);
          Object.keys(data.prompts).forEach((promptId: string) => {
            const promptData = data.prompts[promptId];
            // Handle both string (from Google Sheets) and object (from database) formats
            const template = typeof promptData === 'string' ? promptData : promptData.template;
            updatePromptTemplate(promptId, template, PromptTemplateType.GENERATED, true);
          });
        }
      } catch (error) {
        console.warn("[PromptContext] Could not fetch generated prompts, using JSON defaults:", error);
      }
    }

    const fetchExplicitPrompts = async () => {
      try {
        // Use new platform config API endpoint with hybrid fallback
        const response = await fetch(`${SERVER_ORIGIN}/api/platform/config/prompts?platform=${APP}&environment=${ENVIRONMENT}&promptTemplateType=EXPLICIT`);
        const data = await response.json();

        if (data.prompts && Object.keys(data.prompts).length > 0) {
          console.log(`[PromptContext] Loaded explicit prompts from ${data.source || 'backend'}`);
          Object.keys(data.prompts).forEach((promptId: string) => {
            const promptData = data.prompts[promptId];
            // Handle both string (from Google Sheets) and object (from database) formats
            const template = typeof promptData === 'string' ? promptData : promptData.template;
            updatePromptTemplate(promptId, template, PromptTemplateType.EXPLICIT, true);
          });
        }
      } catch (error) {
        console.warn("[PromptContext] Could not fetch explicit prompts, using JSON defaults:", error);
      }
    }

    const fetchPrompts = async () => {
      await fetchGeneratedPrompts();
      await fetchExplicitPrompts();
      initialFetchCompleteRef.current = true;
      setPromptsFetched(true);
    }

    fetchPrompts();
  }, []);



  // ============================= PROMPT TEMPLATE MANAGEMENT =============================
  const getPromptCategories = useCallback((templateType: PromptTemplateType = PromptTemplateType.EXPLICIT) => {
    switch (templateType) {
      case PromptTemplateType.GENERATED:
        return Array.from(new Set(promptTemplatesGenerated.map(p => p.category)));
      default:
        return Array.from(new Set(promptTemplates.map(p => p.category)));
    }
  }, [promptTemplates, promptTemplatesGenerated]);


  const getPromptTemplate = useCallback((id: string, templateType: PromptTemplateType = PromptTemplateType.EXPLICIT): PromptTemplate | undefined => {
    const currentTemplates = templateType === PromptTemplateType.GENERATED
      ? promptTemplatesGeneratedRef.current
      : promptTemplatesRef.current;

    return currentTemplates.find(p => p.id === id);
  }, []);


  const getPromptTemplatesForCategory = useCallback((category: string, templateType: PromptTemplateType = PromptTemplateType.EXPLICIT): PromptTemplate[] => {
    const currentTemplates = templateType === PromptTemplateType.GENERATED
      ? promptTemplatesGeneratedRef.current
      : promptTemplatesRef.current;

    return currentTemplates.filter(p => p.category === category);
  }, []);


  const getCompiledPrompt = useCallback((id: string, dynamicData?: DynamicPromptData, templateType: PromptTemplateType = PromptTemplateType.EXPLICIT): string => {
    const prompt = getPromptTemplate(id, templateType);
    if (!prompt) {
      console.warn(`Prompt template "${id}" not found`);
      return "";
    }

    if (!dynamicData) return prompt.template;

    let compiledTemplate = prompt.template;

    Object.keys(dynamicData).forEach((key) => {
      const placeholder = `{${key}}`;
      const value = dynamicData[key];
      if (value !== undefined && value !== null) {
        compiledTemplate = compiledTemplate.replaceAll(placeholder, String(value));
      }
    });

    return compiledTemplate;
  }, [getPromptTemplate]);


  const updatePromptTemplate = useCallback((id: string, newTemplate: string, templateType: PromptTemplateType = PromptTemplateType.EXPLICIT, fromBackend: boolean = false) => {
    if (fromBackend && userHasEditedRef.current) return;

    if (!fromBackend) {
      userHasEditedRef.current = true;
    }

    contextCache.current.clear();

    switch (templateType) {
      case PromptTemplateType.GENERATED:
        setPromptTemplatesGenerated(prev => {
          const updated = prev.map(prompt =>
            prompt.id === id ? { ...prompt, template: newTemplate } : prompt
          );
          promptTemplatesGeneratedRef.current = updated;
          return updated;
        });
        break;

      default:
        setPromptTemplates(prev => {
          const updated = prev.map(prompt =>
            prompt.id === id ? { ...prompt, template: newTemplate } : prompt
          );
          promptTemplatesRef.current = updated;
          return updated;
        });
    }
  }, []);


  const updatePromptTemplateResponse = useCallback((id: string, newResponse: string, templateType: PromptTemplateType = PromptTemplateType.EXPLICIT) => {
    switch (templateType) {
      case PromptTemplateType.GENERATED:
        setPromptTemplatesGenerated(prev =>
          prev.map(prompt =>
            prompt.id === id ? { ...prompt, response: newResponse } : prompt
          )
        );
        break;

      default:
        setPromptTemplates(prev =>
          prev.map(prompt =>
            prompt.id === id ? { ...prompt, response: newResponse } : prompt
          )
        );
    }
  }, []);



  // ============================= RESPONSE HANDLING =============================
  const getModelResponse = useCallback(async (context: string, prompt: string, promptType: string = PromptType.QUESTION_ANSWER) => {

    try {
      setChatMessagePending(true);
      const modelResponse = await fetchModelResponse(promptType, context, prompt, getDefaultAppStateHandlerConfig());
      setChatMessagePending(false);

      if (!modelResponse) {
        return SERVER_ERROR_TEXT;
      }

      return modelResponse;
    }
    catch (error) {
      console.error("Error:", error);
      setChatMessagePending(false);
    }
  }, []);


  const getAssistantResponse = useCallback(async ({ context, prompt, file, promptType = PromptType.QUESTION_ANSWER, messageType = MessageType.CHAT }: any) => {

    try {
      const assistantResponse = await fetchAssistantResponse(promptType, context, prompt, file, getDefaultAppStateHandlerConfig());

      if (!assistantResponse) {
        return SERVER_ERROR_TEXT;
      }

      return assistantResponse;
    }
    catch (error) {
      console.error('Error:', error);
    }
  }, []);



  // ============================= PROMPT HANDLING =============================
  const handleQuestion = useCallback(async (question: string, senderName: string, selectedFile?: File) => {

    if (!questionsAllowed) {
      postNotification(QUESTIONS_PAUSED_NOTIFICATION);
      return;
    }

    const currentSlideNumber = getCurrentSlideNumber();
    const currentSlideContext = getSlideContext();
    const sessionTranscript = getSessionTranscript();
    const presentationContext = getSessionPresentationContext();

    // Build context using templates
    let context = getCompiledPrompt("studentPromptContextPreliminary", { slideNumber: currentSlideNumber });

    if (USE_CURRENT_SLIDE_CONTEXT_ONLY && currentSlideContext !== undefined) {
      context += ` \n${getCompiledPrompt("currentSlideContext", { currentSlideContext })}`;
      const currentSlideTranscript = getCurrentSlideTranscript(sessionTranscript, currentSlideNumber);
      context += ` \n${getCompiledPrompt("currentSlideTranscriptContext", { currentSlideTranscript })}`;
    } else {
      context += ` \n${getCompiledPrompt("presentationContext", { presentationContext: condensePresentationContext(presentationContext) })}`;
      context += ` \n${getCompiledPrompt("transcriptContext", { transcript: condenseTranscript(sessionTranscript) })}`;
    }

    if (senderName) {
      context += ` \n${getCompiledPrompt("senderContext", { senderName })}`;
    }

    context += ` \n${getCompiledPrompt("instructorIdentity")}`;

    const prompt = question;

    let message: string;
    if (USE_ASSISTANTS) {
      const assistantResponseOptions = { context, prompt, file: selectedFile };
      message = await getAssistantResponse(assistantResponseOptions);
    }
    else {
      message = await getModelResponse(context, prompt);
    }

    return message;
  }, [questionsAllowed, postNotification, getCompiledPrompt, getCurrentSlideNumber, getSlideContext, getSessionTranscript, getSessionPresentationContext]);


  const handleGeneralPrompt = useCallback(async (prompt: string) => {

    if (!prompt) return;

    const transcript = getSessionTranscript();
    const presentationContext = getSessionPresentationContext();
    const studentName = getHumanStudentName();
    const currentSlideNumber = getCurrentSlideNumber();
    const currentSlideContext = getSlideContext();

    // Update cache key to include slide number for current-slide mode
    const contextKey = USE_CURRENT_SLIDE_CONTEXT_ONLY
      ? `${currentSlideNumber}-${transcript.length}-${studentName}`
      : `${transcript.length}-${presentationContext.length}-${studentName}`;

    let context = contextCache.current.get(contextKey);
    if (!context) {
      context = getCompiledPrompt("generalPromptContextPreliminary");

      if (USE_CURRENT_SLIDE_CONTEXT_ONLY && currentSlideContext !== undefined) {
        context += ` \n${getCompiledPrompt("currentSlideContext", { currentSlideContext })}`;
        const currentSlideTranscript = getCurrentSlideTranscript(transcript, currentSlideNumber);
        context += ` \n${getCompiledPrompt("currentSlideTranscriptContext", { currentSlideTranscript })}`;
      } else {
        context += ` \n${getCompiledPrompt("presentationContext", { presentationContext: condensePresentationContext(presentationContext) })}`;
        context += ` \n${getCompiledPrompt("transcriptContext", { transcript: condenseTranscript(transcript) })}`;
      }

      context += ` \n${getCompiledPrompt("studentNameContext", { studentName })}`;
      context += ` \n${getCompiledPrompt("instructorIdentity")}`;
      context += ` \n${getCompiledPrompt("responseSanitizer")}`;

      contextCache.current.set(contextKey, context);

      if (contextCache.current.size > 10) {
        const firstKey = contextCache.current.keys().next().value;
        contextCache.current.delete(firstKey ?? "");
      }
    }

    let message: string;

    if (USE_ASSISTANTS) {
      const assistantResponseOptions = { context, prompt, messageType: MessageType.CHAT };
      message = await getAssistantResponse(assistantResponseOptions);
    }
    else {
      message = await getModelResponse(context, prompt, PromptType.GENERAL);
    }

    return message;
  }, [getCompiledPrompt, getSessionTranscript, getSessionPresentationContext, getHumanStudentName, getCurrentSlideNumber, getSlideContext]);


  const handleGeneratePrompt = useCallback(async (prompt: string) => {
    const context = "Provide a recommended prompt. Create a prompt. Do not mention creating the prompt or acknowledging this request. Create and return the prompt fully ready to use, no filler text";

    if (!prompt) {
      console.warn("Prompt is empty when handling generate prompt");
      return "";
    }

    const generatedPrompt = await getModelResponse(context, prompt, PromptType.GENERATE);

    return generatedPrompt;
  }, [getModelResponse]);


  const handleExecutePrompt = useCallback(async (prompt: string) => {
    const context = "Execute this prompt. Do not mention executing the prompt or acknowledging this request. Execute and return the response fully ready to use, no filler text";
    const executedPrompt = await getModelResponse(context, prompt, PromptType.GENERAL);
    return executedPrompt;
  }, [getModelResponse]);


  const handleReviseGeneratedPrompt = useCallback(async (revisePrompt: string, generatedPrompt: string) => {
    const defaultRevisePrompt = "Revise this prompt. Do not mention revising the prompt or acknowledging this request. Strictly revise and return the prompt fully ready to use, no filler text";
    const context = `You were given this prompt, this is what needs revised: ${generatedPrompt}`;

    revisePrompt = revisePrompt ? revisePrompt : defaultRevisePrompt;
    const revisedPrompt = await getModelResponse(context, revisePrompt, PromptType.GENERATE);

    return revisedPrompt;
  }, [getModelResponse]);


  const handleGenerateAndRevisePrompt = useCallback(async (prompt: string, revisePrompt: string) => {
    let generatedPrompt = await handleGeneratePrompt(prompt);
    generatedPrompt = await handleReviseGeneratedPrompt(revisePrompt, generatedPrompt);

    return generatedPrompt;
  }, [handleGeneratePrompt, handleReviseGeneratedPrompt]);


  const handleCompileGenerateAndRevisePrompts = useCallback(async (draftPromptId: string, draftDynamicData: DynamicPromptData, revisePromptId: string, reviseDynamicData: DynamicPromptData) => {
    const draftPrompt = getCompiledPrompt(draftPromptId, draftDynamicData, PromptTemplateType.GENERATED);
    const revisePrompt = getCompiledPrompt(revisePromptId, reviseDynamicData, PromptTemplateType.GENERATED);

    let draftedPrompt = await handleGeneratePrompt(draftPrompt);
    let revisedPrompt = await handleReviseGeneratedPrompt(revisePrompt, draftedPrompt);

    updatePromptTemplateResponse(draftPromptId, draftedPrompt, PromptTemplateType.GENERATED);
    updatePromptTemplateResponse(revisePromptId, revisedPrompt, PromptTemplateType.GENERATED);

    return revisedPrompt;
  }, [getCompiledPrompt, handleGeneratePrompt, handleReviseGeneratedPrompt, updatePromptTemplateResponse]);


  const handleCompileGenerateAndPublishPrompts = useCallback(async (draftPromptId: string, draftDynamicData: DynamicPromptData, publishPromptId: string, publishDynamicData: DynamicPromptData) => {
    const draftPrompt = getCompiledPrompt(draftPromptId, draftDynamicData, PromptTemplateType.GENERATED);
    const publishPrompt = getCompiledPrompt(publishPromptId, publishDynamicData, PromptTemplateType.GENERATED);
    const generatedPrompt = await handleGeneratePrompt(draftPrompt);

    const publishedPrompt = await handleReviseGeneratedPrompt(publishPrompt, generatedPrompt);

    updatePromptTemplateResponse(draftPromptId, generatedPrompt, PromptTemplateType.GENERATED);
    updatePromptTemplateResponse(publishPromptId, publishedPrompt, PromptTemplateType.GENERATED);

    return publishedPrompt;
  }, [getCompiledPrompt, handleGeneratePrompt, handleReviseGeneratedPrompt, updatePromptTemplateResponse]);


  const handleCompileAndExecutePrompt = useCallback(async (promptId: string, dynamicData: DynamicPromptData, promptType: PromptTemplateType = PromptTemplateType.EXPLICIT) => {
    const compiledPrompt = getCompiledPrompt(promptId, dynamicData, promptType);
    const executedPrompt = await handleExecutePrompt(compiledPrompt);
    return executedPrompt;
  }, [getCompiledPrompt, handleExecutePrompt]);


  // ============================= GET ALL PROMPTS =============================
  const getAllPrompts = useCallback((templateType: PromptTemplateType = PromptTemplateType.EXPLICIT): PromptTemplate[] => {
    const currentTemplates = templateType === PromptTemplateType.GENERATED
      ? promptTemplatesGeneratedRef.current
      : promptTemplatesRef.current;

    return currentTemplates;
  }, []);


  // ============================= SAVE PROMPTS TO DATABASE =============================
  const savePromptsToDatabase = useCallback(async (platform: string, environment: string): Promise<boolean> => {
    try {
      // Save both explicit and generated prompts
      const explicitPrompts = promptTemplatesRef.current;
      const generatedPrompts = promptTemplatesGeneratedRef.current;

      // Convert prompt templates to database format
      const convertToDbFormat = (templates: PromptTemplate[]) => {
        const result: any = {};
        templates.forEach(template => {
          result[template.id] = {
            template: template.template,
            description: template.description,
            name: template.name,
            category: template.category,
            response: template.response
          };
        });
        return result;
      };

      // Save explicit prompts
      const explicitPromptsDb = convertToDbFormat(explicitPrompts);
      const explicitResponse = await fetch(`${SERVER_ORIGIN}/api/admin/prompts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform: platform.toLowerCase(),
          environment: environment.toLowerCase(),
          promptTemplateType: 'EXPLICIT',
          prompts: explicitPromptsDb
        })
      });

      if (!explicitResponse.ok) {
        console.error('[PromptContext] Failed to save explicit prompts:', await explicitResponse.text());
        return false;
      }

      // Save generated prompts
      const generatedPromptsDb = convertToDbFormat(generatedPrompts);
      const generatedResponse = await fetch(`${SERVER_ORIGIN}/api/admin/prompts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform: platform.toLowerCase(),
          environment: environment.toLowerCase(),
          promptTemplateType: 'GENERATED',
          prompts: generatedPromptsDb
        })
      });

      if (!generatedResponse.ok) {
        console.error('[PromptContext] Failed to save generated prompts:', await generatedResponse.text());
        return false;
      }

      console.log(`[PromptContext] Successfully saved prompts to ${platform}/${environment}`);
      return true;
    } catch (error) {
      console.error('[PromptContext] Error saving prompts to database:', error);
      return false;
    }
  }, []);


  // ============================= CONTEXT VALUES =============================
  const contextValue: PromptContextType = {
    promptTemplates,
    promptsFetched,
  };

  const updateContextValue: PromptUpdateContextType = {
    handleQuestion,
    handleGeneralPrompt,
    handleGeneratePrompt,
    handleReviseGeneratedPrompt,
    handleGenerateAndRevisePrompt,
    handleExecutePrompt,
    handleCompileAndExecutePrompt,

    getCompiledPrompt,
    getPromptCategories,
    getPromptTemplate,
    getPromptTemplatesForCategory,
    updatePromptTemplate,
    updatePromptTemplateResponse,
    handleCompileGenerateAndRevisePrompts,
    handleCompileGenerateAndPublishPrompts,

    getAllPrompts,
    savePromptsToDatabase,
  };


  return (
    <PromptContext.Provider value={contextValue}>
      <PromptUpdateContext.Provider value={updateContextValue}>
        {children}
      </PromptUpdateContext.Provider>
    </PromptContext.Provider>
  );

}



// ============================= HELPER FUNCTIONS =============================
function condenseTranscript(currentTranscript: string) {
  const TRANSCRIPT_CHARACTER_LIMIT = 9000;
  if (currentTranscript.length > TRANSCRIPT_CHARACTER_LIMIT) {
    return currentTranscript.slice(currentTranscript.length - TRANSCRIPT_CHARACTER_LIMIT);
  }
  return currentTranscript;
}

function condensePresentationContext(currentPresentationContext: string) {
  const PRESENTATION_CONTEXT_CHARACTER_LIMIT = 5000;
  if (currentPresentationContext.length > PRESENTATION_CONTEXT_CHARACTER_LIMIT) {
    return currentPresentationContext.slice(currentPresentationContext.length - PRESENTATION_CONTEXT_CHARACTER_LIMIT);
  }
  return currentPresentationContext;
}

function getCurrentSlideTranscript(fullTranscript: string, currentSlideNumber: number): string {
  if (!fullTranscript) return "";

  const slideNumberPattern = new RegExp(`\\[\\[${currentSlideNumber}\\|([^:]*):\\s*([^\\]]+)\\]\\]`, 'g');
  const matches = [];
  let match;

  while ((match = slideNumberPattern.exec(fullTranscript)) !== null) {
    matches.push(`[[${currentSlideNumber}|${match[1]}: ${match[2]}]]`);
  }

  return matches.join(' ');
}


// ============================= EXPORTS =============================
export {
  PromptTemplateType,
  PromptContextProvider,
  usePrompt,
  usePromptUpdate
};
